import React, { useEffect, useRef, useState } from 'react';
import {
	Button,
	message,
	Select,
	Table,
	Modal,
	Form,
	Input,
	notification,
	Row,
	Col,
	Alert,
	Typography,
} from 'antd';
import { EyeInvisibleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import {
	getImportedTwilioNumbers,
	removeClientNumber,
	assignClientNumber,
	UpdateResellerBranding,
	updateTutorialStepStatus,
	updateTourStatus,
} from '@/api/reseller';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useGetResellerClients } from 'src/Hooks/resellerHooks';
import { CustomModal } from '@/components/CustomModal';
import { Circle } from '@mui/icons-material';
import { BookOpenIcon, PhoneIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';

import '../ResellerDashboard.scss';
import ResellerTour from '../ResellerTour/ResellerTour';
import TourButton from '@/components/TourButton/TourButton';
import { AppRoutes } from 'src/enums/ERoutes';
import { setTour } from '@/store/resellerTour/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { getResellerTourState } from '@/store/resellerTour/selector';

const { Option } = Select;
const { Title, Text } = Typography;

interface TwilioNumber {
	phoneNumber: string;
	friendlyName: string;
	sid: string;
	dateCreated: string;
	capabilities: {
		fax: boolean;
		voice: boolean;
		sms: boolean;
		mms: boolean;
	};
}

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<PhoneIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Bring your own Twilio',
		description: 'Connecting AI Frontdesk with Twilio - Import your Own Twilio',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/connecting-ai-frontdesk-with-twilio-import-your-own-twilio',
	},
];

const ImportPhoneNumbers: React.FC = () => {
	const [clients, setClients] = useState<any[]>([]);
	const [selectedClient, setSelectedClient] = useState<any>(null);
	const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
	const [isConfirmationModalActive, setIsConfirmationModalActive] = useState<boolean>(false);
	const [showTwilioAccountId, setShowTwilioAccountId] = useState<boolean>(false);
	const [showTwilioAccessToken, setShowTwilioAccessToken] = useState<boolean>(false);
	const [addTwilioCredentials, setAddTwilioCredentials] = useState<boolean>(false);
	const [showTwilioModal, setShowTwilioModal] = useState<boolean>(false);
	const [showAssignNumberModal, setShowAssignNumberModal] = useState<boolean>(false);
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);

	const openAssignNumberModal = () => setShowAssignNumberModal(true);
	const closeAssignNumberModal = () => setShowAssignNumberModal(false);
	const openTwilioModal = () => setShowTwilioModal(true);
	const closeTwilioModal = () => setShowTwilioModal(false);

	const dispatch = useAppDispatch();

	const [form] = Form.useForm();
	const queryClient = useQueryClient();

	const byotTourRef1 = useRef<HTMLDivElement>(null);
	const byotTourRef2 = useRef<HTMLDivElement>(null);

	const byotTourSteps = [
		{
			title: <h1 className="font-bold text-2xl">Connect your own Twilio account</h1>,
			description: (
				<p className="text-base">
					We now offer you a chance to connect your own Twilio account as well. If you want to use
					your own numbers for rerouting, you will do it here.
				</p>
			),
			target: byotTourRef1.current as HTMLElement,
		},
		{
			title: <h1 className="font-bold text-2xl">Check out our step-by-step Tutorial</h1>,
			description: (
				<p className="text-base">
					Need help? Click the Tutorials tab on the right for a detailed walkthrough on how to
					connect your Twilio account.
				</p>
			),
			target: byotTourRef2.current as HTMLElement,
		},
	];

	const mutation = useMutation({
		mutationKey: ['updateResellerBranding'],
		mutationFn: UpdateResellerBranding,
		onError(data) {
			notification.error({ message: data.message });
		},
		onSuccess(data) {
			if (data.success) {
				queryClient.invalidateQueries({ queryKey: ['reseller'] });
				notification.success({
					key: 'update-reseller',
					message: 'Updated successfully',
					duration: 3,
				});
			}
		},
	});

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const handleSubmit = async (values: { twilioAccessToken: string; twilioAccountId: string }) => {
		await mutation.mutate({
			emailAddress: resellerData?.emailAddress as string,
			twilioAccessToken: values.twilioAccessToken,
			twilioAccountId: values.twilioAccountId,
		});
	};

	const resellerData = useAppSelector(getResellerData);

	useEffect(() => {
		if (resellerData) {
			form.setFieldsValue({
				twilioAccountId: resellerData?.inboundTwilioConfig?.accountId,
				twilioAccessToken: resellerData?.inboundTwilioConfig?.accessToken,
			});
			setAddTwilioCredentials(
				!resellerData?.inboundTwilioConfig?.accessToken ||
					!resellerData?.inboundTwilioConfig?.accountId,
			);
		}
	}, [resellerData, form]);

	const maskTwilioAccountId = (id: string): string => {
		if (id.length <= 8) {
			return id;
		}
		return id.slice(0, 4) + 'X'.repeat(id.length - 8) + id.slice(-4);
	};

	const getClientsMutation = useGetResellerClients({
		shouldFetchReservedNumbers: true,
		onSuccess: (data) => setClients(data.items),
	});

	const {
		data: twilioNumberData,
		isFetching: isLoadingTwilioNumbers,
		refetch: refetchTwilioNumbers,
	} = useQuery<{ numbers: TwilioNumber[] }>({
		queryKey: ['twilioNumbers', resellerData?.emailAddress],
		queryFn: getImportedTwilioNumbers,
		enabled: !!resellerData?.emailAddress,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const updateLocalClients = (clientUsername: string, newReservedNumber: string | null) => {
		setClients((prevClients) =>
			prevClients.map((client) =>
				client.number === clientUsername
					? { ...client, reservedNumber: newReservedNumber }
					: client,
			),
		);
	};

	const resetForm = () => {
		setSelectedClient(null);
		setSelectedNumber(null);
	};

	const removeClientNumberMutation = useMutation({
		mutationFn: removeClientNumber,
		onSuccess: (_, variables) => {
			updateLocalClients(variables.clientUsername, null);
			message.success('Number removed successfully');
			setIsConfirmationModalActive(false);
			resetForm();
		},
		onError: () => {
			message.error('Error removing the phone number');
		},
	});

	const assignClientNumberMutation = useMutation({
		mutationFn: assignClientNumber,
		onSuccess: (_, variables) => {
			updateLocalClients(variables.clientUsername, variables.clientNumber);
			message.success('Number assigned successfully');
			resetForm();
			form.resetFields();
		},
		onError: () => {
			message.error('Error assigning the phone number');
		},
	});

	const handleClientSelect = (value: string) => {
		const selected = clients.find((client) => client.number === value);
		setSelectedClient(selected);
		setSelectedNumber(null);
	};

	const handleNumberSelect = (value: string) => setSelectedNumber(value);

	const handleApply = () => {
		if (!selectedNumber || !selectedClient?.number) {
			message.error('Please select a sub-account and a number');
			return;
		}
		assignClientNumberMutation.mutate({
			clientUsername: selectedClient.number,
			clientNumber: selectedNumber,
		});
	};

	const handleRemoveNumber = () => {
		if (!selectedClient?.number || !selectedClient?.reservedNumber) {
			message.error('Invalid operation');
			return;
		}
		removeClientNumberMutation.mutate({
			clientUsername: selectedClient.number,
			clientNumber: selectedClient.reservedNumber,
		});
	};

	const handleRefreshClients = () => {
		if (resellerData?.emailAddress) {
			getClientsMutation.mutate();
		}
	};

	const handleRefreshNumbers = () => refetchTwilioNumbers();

	const columns = [
		{
			title: 'Username',
			dataIndex: ['accountInfo', 'username'],
			key: 'username',
		},
		{
			title: 'Email',
			dataIndex: ['accountInfo', 'emailAddress'],
			key: 'email',
		},
		{
			title: 'Reserved Number',
			dataIndex: 'reservedNumber',
			key: 'reservedNumber',
		},
	];

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (resellerData?.emailAddress) {
			getClientsMutation.mutate();
		}
	}, [resellerData?.emailAddress]);

	useEffect(() => {
		if (!resellerData?.emailAddress || resellerTour.open) {
			return;
		}

		if (!resellerData?.tours?.twilio) {
			updateToursMutation.mutate('twilio');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_IMPORT_NUMBER,
				}),
			);
		}
	}, [resellerData?.emailAddress, resellerTour.open]);

	const handleTutorialClick = () => {
		if (!resellerData?.tutorial_steps?.import_twilio_tutorial) {
			updateTutorialMutation.mutate('import_twilio_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!resellerData?.tours?.twilio) {
			updateToursMutation.mutate('twilio');
		}
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.IMPORT_NUMBER}>
			<div className="p-4">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Bring your own Twilio
						</Title>
						<Text type="secondary">Import your Twilio numbers to AI Frontdesk</Text>
					</div>

					<div ref={byotTourRef2} className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							className={
								!resellerData?.tutorial_steps?.import_twilio_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
							onClick={handleTutorialClick}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
				</div>
			</div>
			<div className="h-full bg-gray-50 p-6">
				{addTwilioCredentials ? (
					<div className="h-full bg-gray-50 p-6">
						<Row className="justify-center">
							<Col span={18}>
								<div className="shadow bg-white p-5 rounded-xl">
									<h3 className="text-2xl font-bold">No Twilio Connected</h3>
									<p className="text-gray-600">
										Want to use your existing Phone numbers? Connect your account to assign numbers
										directly to receptionists instead of having customers reserve them from us.
									</p>
									<Row className="mt-10 mb-5" gutter={16}>
										<Col span={12}>
											<div className="bg-gray-100 rounded-xl p-5">
												<h4 className="text-lg font-bold text-gray-600">Standard Numbers</h4>
												<ul className="list-disc pl-4 mt-4">
													<li className="mb-3 text-gray-600">Customers reserve numbers</li>
													<li className="mb-3 text-gray-600">Automatic provisioning</li>
													<li className="mb-3 text-gray-600">We handle the setup</li>
													<li className="mb-3 text-gray-600">Works out of the box</li>
												</ul>
											</div>
										</Col>
										<Col span={12}>
											<div className="bg-gray-200 rounded-xl p-5">
												<h4 className="text-lg font-bold text-dark-600">Bring Your Own Twillo</h4>
												<ul className="list-disc pl-4 mt-4">
													<li className="mb-3 text-gray-600">Use your Twilio numbers</li>
													<li className="mb-3 text-gray-600">Assign numbers directly</li>
													<li className="mb-3 text-gray-600">Manage your own numbers</li>
													<li className="mb-3 text-gray-600">Full control over allocation</li>
												</ul>
											</div>
										</Col>
									</Row>
									<div ref={byotTourRef1} className="rounded-xl p-4 bg-gray-100">
										<div className="flex justify-between items-center">
											<div>
												<h3 className="text-base">Ready to bring your Twillo numbers?</h3>
												<p className="text-gray-600 my-0">
													Connect your account to start using your numbers with our platform.
												</p>
											</div>
											<Button
												onClick={openTwilioModal}
												shape="round"
												size="large"
												className="bg-blue-600 text-white"
											>
												Connect Twilio
											</Button>
										</div>
									</div>
								</div>
							</Col>
						</Row>
					</div>
				) : (
					<div className="h-full bg-gray-50 p-6">
						<div className="flex justify-between items-center bg-gray-100 p-3 rounded-lg">
							<div className="flex items-center">
								<Circle className="text-green-600" />
								<div className="ml-3">
									<h4 className="text-lg my-0">Connected to Twilio</h4>
									<p className="my-0 text-gray-600">
										Account ID:{' '}
										{maskTwilioAccountId(resellerData?.inboundTwilioConfig?.accountId || '')}.{' '}
										{twilioNumberData?.numbers.length} numbers available.
									</p>
								</div>
							</div>
							<Button shape="round" size="large" onClick={openTwilioModal}>
								Manage Connection
							</Button>
						</div>
						<div className="mt-10 mb-5">
							<div className="flex justify-between items-center">
								<h4 className="text-lg my-0">Assigned Numbers</h4>
								<Button
									onClick={openAssignNumberModal}
									shape="round"
									size="large"
									className="bg-blue-600 text-white"
								>
									Assign New Number
								</Button>
							</div>
						</div>
						<Table
							dataSource={clients}
							columns={columns}
							rowKey="number"
							loading={getClientsMutation.isPending}
							pagination={{
								total: clients.length,
								pageSize: 10,
								showTotal: (total) => `Total ${total} items`,
							}}
						/>
					</div>
				)}
			</div>

			{/* Twilio Modal */}
			<Modal footer={null} open={showTwilioModal} width={600} onCancel={closeTwilioModal}>
				<h3 className="text-xl font-semibold">Connect Your Twilio Account</h3>
				<div className="bg-gray-100 p-3 rounded-xl mt-4">
					<div className="flex justify-between">
						<p className="my-0 text-sm">
							You can find your Account SID and Auth Token in your Twilio Console under Account -
							API Keys & Tokens.
						</p>
						<Button
							shape="round"
							onClick={() =>
								window.open(
									'https://help.twilio.com/articles/14726256820123-What-is-a-Twilio-Account-SID-and-where-can-I-find-it-',
									'_blank',
								)
							}
						>
							View Guide
						</Button>
					</div>
				</div>
				<Form form={form} onFinish={handleSubmit} layout="vertical" className="mt-6">
					<Form.Item
						label="Account SID"
						name="twilioAccountId"
						rules={[
							{
								validator: (_, value) => {
									if (value && /\s/.test(value)) {
										return Promise.reject('Spaces are not allowed');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input.Password
							size="large"
							placeholder="Enter your account SID "
							iconRender={(visible) =>
								visible ? (
									<EyeOutlined className="text-gray-500" />
								) : (
									<EyeInvisibleOutlined className="text-gray-500" />
								)
							}
							visibilityToggle={{
								visible: showTwilioAccountId,
								onVisibleChange: setShowTwilioAccountId,
							}}
							className="rounded-lg"
						/>
					</Form.Item>
					<Form.Item
						label="Auth token"
						name="twilioAccessToken"
						rules={[
							{
								validator: (_, value) => {
									if (value && /\s/.test(value)) {
										return Promise.reject('Spaces are not allowed');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input.Password
							size="large"
							placeholder="Enter your auth token"
							iconRender={(visible) =>
								visible ? (
									<EyeOutlined className="text-gray-500" />
								) : (
									<EyeInvisibleOutlined className="text-gray-500" />
								)
							}
							visibilityToggle={{
								visible: showTwilioAccessToken,
								onVisibleChange: setShowTwilioAccessToken,
							}}
							className="rounded-lg"
						/>
					</Form.Item>
					<Alert
						message="Important"
						description="Make sure you're using your live Twilio credentials, not test ones."
						type="warning"
						className="p-3 my-7"
					/>
					<div className="flex justify-between items-center">
						<Button
							type="default"
							shape="round"
							size="large"
							onClick={() => setAddTwilioCredentials(false)}
						>
							Cancel
						</Button>
						<Button
							size="large"
							shape="round"
							className="bg-blue-600 text-white"
							loading={mutation.isPending}
							onClick={() => handleSubmit(form.getFieldsValue())}
						>
							Connect
						</Button>
					</div>
				</Form>
			</Modal>

			{/* Assign Number Modal */}
			<Modal
				footer={null}
				open={showAssignNumberModal}
				onCancel={closeAssignNumberModal}
				width={600}
			>
				<h3 className="text-xl font-semibold">Assign Phone Number</h3>
				<p className="text-gray-600 my-0">
					Select an account and assign one of your Twilio numbers
				</p>
				<Form form={form} onFinish={handleApply} layout="vertical" className="mt-6">
					<Form.Item label="Select Account" name="clientNumber">
						<div className="flex gap-2">
							<Select
								showSearch
								className="flex-1"
								placeholder="Select a sub-account"
								optionFilterProp="children"
								onChange={handleClientSelect}
								value={selectedClient?.number}
								loading={getClientsMutation.isPending}
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{clients.map((client) => (
									<Option key={client.number} value={client.number}>
										{client?.accountInfo?.username}
									</Option>
								))}
							</Select>
							<Button
								icon={<ReloadOutlined />}
								onClick={handleRefreshClients}
								loading={getClientsMutation.isPending}
								size="middle"
							/>
						</div>
					</Form.Item>
					<Form.Item label="Select Phone Number" name="phoneNumber">
						<div className="flex gap-2">
							<Select
								showSearch
								className="flex-1"
								placeholder="Select a phone number"
								optionFilterProp="children"
								onChange={handleNumberSelect}
								value={selectedNumber}
								loading={isLoadingTwilioNumbers}
								disabled={!selectedClient && getClientsMutation.isPending}
								filterOption={(input, option) =>
									(option?.children as unknown as string)
										.toLowerCase()
										.includes(input.replace(/\s|-|\(|\)/g, '').toLowerCase())
								}
							>
								{twilioNumberData?.numbers?.map((number) => (
									<Option key={number.sid} value={number.phoneNumber}>
										{number.phoneNumber}
									</Option>
								))}
							</Select>
							<Button
								icon={<ReloadOutlined />}
								onClick={handleRefreshNumbers}
								loading={isLoadingTwilioNumbers}
								size="middle"
							/>
						</div>
					</Form.Item>
					<Form.Item>
						<Button
							htmlType="submit"
							className="w-full rounded-lg bg-blue-600 text-white mt-4"
							size="large"
							shape="round"
							onClick={handleApply}
							loading={assignClientNumberMutation.isPending}
							disabled={!selectedClient || !selectedNumber}
						>
							Assign Number
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			{/* Remove Number Modal */}
			<CustomModal
				title="Are you sure?"
				visible={isConfirmationModalActive}
				cancelAction={() => {
					setIsConfirmationModalActive(false);
					setSelectedNumber(null);
				}}
				footer={[
					<Button
						key="back"
						onClick={() => {
							setIsConfirmationModalActive(false);
							setSelectedNumber(null);
						}}
					>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						danger
						onClick={handleRemoveNumber}
						loading={removeClientNumberMutation.isPending}
					>
						Remove
					</Button>,
				]}
			>
				<p>
					Are you sure you want to remove the phone number? This will disassociate the phone number
					from the account until you assign it back.
				</p>
			</CustomModal>
			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			>
				<TourButton onClick={handleTourClick} page="twilio" />
			</TutorialsModal>
			<ResellerTour tourSteps={byotTourSteps} />
		</ResellerDashboard>
	);
};

export default ImportPhoneNumbers;
